import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Book, Shield, Zap, HelpCircle, Mail, Lightbulb, Wrench, Search, Heart, Target, Megaphone, Send, CheckCircle, Loader2 } from 'lucide-react';

const HelpPage = ({ auth }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [contactForm, setContactForm] = useState({
    name: auth?.user?.name || '',
    email: auth?.user?.email || '',
    category: 'General',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support/contact`, contactForm);
      setSubmitted(true);
      toast.success('Your support request has been submitted!');
      // Reset form after success
      setContactForm({
        name: auth?.user?.name || '',
        email: auth?.user?.email || '',
        category: 'General',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const dimensions = [
    { code: 'B', name: 'Brainstorm', icon: Lightbulb, color: 'text-purple-600', bg: 'bg-purple-100', description: 'Visionary thinking and asking big questions' },
    { code: 'U', name: 'Uncover', icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-100', description: 'Creating and improving solutions' },
    { code: 'I', name: 'Inspect', icon: Search, color: 'text-green-600', bg: 'bg-green-100', description: 'Evaluating and making sound decisions' },
    { code: 'L', name: 'Lift', icon: Heart, color: 'text-pink-600', bg: 'bg-pink-100', description: 'Supporting and empowering others' },
    { code: 'D', name: 'Deliver', icon: Target, color: 'text-orange-600', bg: 'bg-orange-100', description: 'Driving results and execution' },
    { code: 'R', name: 'Rally', icon: Megaphone, color: 'text-red-600', bg: 'bg-red-100', description: 'Motivating and mobilizing people' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(auth?.user ? '/dashboard' : '/')}
            className="flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-gray-800" data-testid="help-title">Help Center</h1>
            <p className="text-gray-600">Everything you need to know about BUILDR</p>
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:grid-cols-5">
            <TabsTrigger value="getting-started" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Getting Started</span>
              <span className="sm:hidden">Start</span>
            </TabsTrigger>
            <TabsTrigger value="user-guide" className="flex items-center gap-2">
              <Book className="h-4 w-4" />
              <span className="hidden sm:inline">User Guide</span>
              <span className="sm:hidden">User</span>
            </TabsTrigger>
            <TabsTrigger value="admin-guide" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Admin Guide</span>
              <span className="sm:hidden">Admin</span>
            </TabsTrigger>
            <TabsTrigger value="faq" className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              <span>FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Contact</span>
              <span className="sm:hidden">Contact</span>
            </TabsTrigger>
          </TabsList>

          {/* Getting Started Tab */}
          <TabsContent value="getting-started" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-indigo-600" />
                  Quick Start Guide
                </CardTitle>
                <CardDescription>Get up and running with BUILDR in minutes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Steps */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-indigo-50 rounded-lg">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
                    <h3 className="font-semibold text-lg mb-2">Create Account</h3>
                    <p className="text-gray-600 text-sm">Register as an individual or with an organization code from your church/company.</p>
                  </div>
                  <div className="text-center p-6 bg-purple-50 rounded-lg">
                    <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
                    <h3 className="font-semibold text-lg mb-2">Purchase Assessment</h3>
                    <p className="text-gray-600 text-sm">Buy credits to take the assessment. Single assessment is $10, or save with bulk packs.</p>
                  </div>
                  <div className="text-center p-6 bg-pink-50 rounded-lg">
                    <div className="w-12 h-12 bg-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
                    <h3 className="font-semibold text-lg mb-2">Discover Your Strengths</h3>
                    <p className="text-gray-600 text-sm">Complete the 42-question assessment and receive your personalized BUILDR profile.</p>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Pricing</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-indigo-600">$10</div>
                      <div className="text-sm text-gray-600">1 Credit</div>
                      <Badge variant="outline" className="mt-2">Individual</Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-indigo-600">$90</div>
                      <div className="text-sm text-gray-600">10 Credits</div>
                      <Badge variant="outline" className="mt-2">Small Pack</Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-indigo-600">$425</div>
                      <div className="text-sm text-gray-600">50 Credits</div>
                      <Badge variant="outline" className="mt-2">Medium Pack</Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-indigo-600">$800</div>
                      <div className="text-sm text-gray-600">100 Credits</div>
                      <Badge variant="outline" className="mt-2">Large Pack</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BUILDR Framework */}
            <Card>
              <CardHeader>
                <CardTitle>The BUILDR Framework</CardTitle>
                <CardDescription>Six dimensions that define how you contribute to teams</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dimensions.map((dim) => (
                    <div key={dim.code} className={`p-4 rounded-lg ${dim.bg}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <dim.icon className={`h-6 w-6 ${dim.color}`} />
                        <div>
                          <span className="font-bold text-lg">{dim.code}</span>
                          <span className="text-gray-600 ml-2">{dim.name}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">{dim.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Guide Tab */}
          <TabsContent value="user-guide" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Book className="h-5 w-5 text-indigo-600" />
                  User Guide
                </CardTitle>
                <CardDescription>Complete guide for taking and understanding your assessment</CardDescription>
              </CardHeader>
              <CardContent className="prose prose-indigo max-w-none">
                <div className="space-y-8">
                  {/* Registration */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Creating Your Account</h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-700">Individual Registration</h4>
                        <ol className="list-decimal list-inside text-gray-600 mt-2 space-y-1">
                          <li>Click "Get Started - Take the Assessment"</li>
                          <li>Select "Individual" registration type</li>
                          <li>Fill in your name, email, and password</li>
                          <li>Click "Create Account"</li>
                        </ol>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700">Joining an Organization</h4>
                        <ol className="list-decimal list-inside text-gray-600 mt-2 space-y-1">
                          <li>Select "Individual" registration type</li>
                          <li>Check "I have an organization code"</li>
                          <li>Enter the 8-character code from your admin</li>
                          <li>Complete registration</li>
                        </ol>
                      </div>
                    </div>
                  </section>

                  {/* Taking Assessment */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Taking the Assessment</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 mb-4">The assessment consists of 42 questions (7 per dimension) and takes approximately 15-20 minutes.</p>
                      <h4 className="font-medium text-gray-700 mb-2">Tips for Best Results:</h4>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        <li>Find a quiet, uninterrupted space</li>
                        <li>Answer based on your natural tendencies, not ideal behaviors</li>
                        <li>Go with your first instinct</li>
                        <li>There are no right or wrong answers</li>
                        <li>Rate each statement on a 1-5 scale (Strongly Disagree to Strongly Agree)</li>
                      </ul>
                    </div>
                  </section>

                  {/* Understanding Results */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Understanding Your Results</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 mb-4">After completing the assessment, you'll receive:</p>
                      <ul className="list-disc list-inside text-gray-600 space-y-2">
                        <li><strong>Pairing Code:</strong> A two-letter code (e.g., "RD") representing your top two strengths</li>
                        <li><strong>Primary Dimension:</strong> Your strongest natural ability</li>
                        <li><strong>Secondary Dimension:</strong> Your supporting strength</li>
                        <li><strong>Score Breakdown:</strong> How you scored across all 6 dimensions</li>
                        <li><strong>Role Recommendations:</strong> Suggested roles for ministry and marketplace</li>
                      </ul>
                    </div>
                  </section>

                  {/* Downloading Reports */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Downloading Reports</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 mb-4">Three report types are available:</p>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded border">
                          <Badge className="bg-indigo-600 mb-2">Ministry Report</Badge>
                          <p className="text-sm text-gray-600">Church and volunteer role recommendations</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <Badge className="bg-emerald-600 mb-2">Marketplace Report</Badge>
                          <p className="text-sm text-gray-600">Business and professional role recommendations</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <Badge className="bg-purple-600 mb-2">Complete Report</Badge>
                          <p className="text-sm text-gray-600">Both Ministry and Marketplace combined</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Guide Tab */}
          <TabsContent value="admin-guide" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-indigo-600" />
                  Administrator Guide
                </CardTitle>
                <CardDescription>Managing your organization's assessments and users</CardDescription>
              </CardHeader>
              <CardContent className="prose prose-indigo max-w-none">
                <div className="space-y-8">
                  {/* Admin Capabilities */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Organization Admin Capabilities</h3>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Manage users within your organization</li>
                        <li>• View all assessment results</li>
                        <li>• Download user reports</li>
                        <li>• Edit user details and credits</li>
                        <li>• Share organization join code</li>
                      </ul>
                    </div>
                  </section>

                  {/* Organization Management */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Organization Management</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-2">Organization Join Code</h4>
                      <p className="text-gray-600 mb-4">Your organization has a unique 8-character join code. Share this with members so they can join your organization during registration.</p>
                      <div className="bg-white p-3 rounded border">
                        <p className="text-sm text-gray-600"><strong>Finding Your Code:</strong> Admin Dashboard → Organization Info Card</p>
                      </div>
                    </div>
                  </section>

                  {/* User Management */}
                  <section>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">User Management</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-2">Available Actions</h4>
                      <div className="grid md:grid-cols-2 gap-3 mt-3">
                        <div className="bg-white p-3 rounded border">
                          <strong className="text-gray-800">View Details</strong>
                          <p className="text-sm text-gray-600">See complete assessment results and score breakdown</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <strong className="text-gray-800">Edit User</strong>
                          <p className="text-sm text-gray-600">Modify name, email, credits, or role</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <strong className="text-gray-800">Download Report</strong>
                          <p className="text-sm text-gray-600">Download PDF reports for any user</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <strong className="text-gray-800">Delete User</strong>
                          <p className="text-sm text-gray-600">Remove user and their assessment data</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-indigo-600" />
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* General FAQs */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg text-gray-800 border-b pb-2">General</h3>
                    
                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        How long does the assessment take?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        The assessment takes approximately 15-20 minutes to complete. There are 42 questions total.
                      </div>
                    </details>

                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        Can I retake the assessment?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        Yes, you can retake the assessment, but each retake costs 1 credit. Your previous results will be replaced with the new results.
                      </div>
                    </details>

                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        Is my data secure?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        Yes, all data is encrypted and stored securely. We never share your personal information with third parties.
                      </div>
                    </details>

                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        Do credits expire?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        No, your credits never expire. You can use them anytime.
                      </div>
                    </details>
                  </div>

                  {/* Payment FAQs */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg text-gray-800 border-b pb-2">Payment</h3>
                    
                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        What payment methods are accepted?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        We accept all major credit and debit cards through Stripe's secure payment system.
                      </div>
                    </details>

                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        How do I use a promo code?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        When purchasing credits, click "Have a promo code?" and enter your code. The discounted price will be displayed before you complete the purchase.
                      </div>
                    </details>
                  </div>

                  {/* Technical FAQs */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg text-gray-800 border-b pb-2">Technical</h3>
                    
                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        My PDF won't download. What should I do?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        Try using a different browser, or clear your browser cache and try again. Make sure pop-ups are enabled for this site.
                      </div>
                    </details>

                    <details className="group bg-gray-50 rounded-lg">
                      <summary className="flex justify-between items-center cursor-pointer p-4 font-medium">
                        The assessment won't submit. How can I fix this?
                        <span className="transition group-open:rotate-180">▼</span>
                      </summary>
                      <div className="px-4 pb-4 text-gray-600">
                        Ensure you've answered all 42 questions. The progress bar should show 100%. Check your internet connection and try again.
                      </div>
                    </details>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Still need help?</h3>
                    <p className="text-gray-600">
                      <button 
                        onClick={() => setActiveTab('contact')} 
                        className="text-indigo-600 hover:underline font-medium"
                      >
                        Submit a support request
                      </button>
                      {' '}or email us at{' '}
                      <a href="mailto:support@builtonpurpose.me" className="text-indigo-600 hover:underline">support@builtonpurpose.me</a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Support Tab */}
          <TabsContent value="contact" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-600" />
                  Contact Support
                </CardTitle>
                <CardDescription>
                  Have a question or need assistance? Fill out the form below and we'll get back to you within 24-48 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Request Submitted!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for contacting us. You'll receive a confirmation email shortly.<br />
                      We typically respond within 24-48 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Submit Another Request
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact-name">Your Name *</Label>
                        <Input
                          id="contact-name"
                          data-testid="contact-name"
                          placeholder="John Doe"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email Address *</Label>
                        <Input
                          id="contact-email"
                          data-testid="contact-email"
                          type="email"
                          placeholder="john@example.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact-category">Category</Label>
                        <Select
                          value={contactForm.category}
                          onValueChange={(value) => setContactForm({ ...contactForm, category: value })}
                        >
                          <SelectTrigger data-testid="contact-category">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="General">General Question</SelectItem>
                            <SelectItem value="Technical">Technical Issue</SelectItem>
                            <SelectItem value="Billing">Billing & Payments</SelectItem>
                            <SelectItem value="Assessment">Assessment Help</SelectItem>
                            <SelectItem value="Organization">Organization Setup</SelectItem>
                            <SelectItem value="Feedback">Feedback & Suggestions</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="contact-subject">Subject *</Label>
                        <Input
                          id="contact-subject"
                          data-testid="contact-subject"
                          placeholder="Brief description of your request"
                          value={contactForm.subject}
                          onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        data-testid="contact-message"
                        placeholder="Please describe your question or issue in detail..."
                        rows={6}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">* Required fields</p>
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="contact-submit"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Additional Contact Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Email Us Directly</h3>
                      <p className="text-gray-600 text-sm mt-1">
                        <a href="mailto:support@builtonpurpose.me" className="text-indigo-600 hover:underline">
                          support@builtonpurpose.me
                        </a>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Response Time</h3>
                      <p className="text-gray-600 text-sm mt-1">
                        We typically respond within 24-48 hours during business days.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>BUILDR and its assessment frameworks are proprietary. © {new Date().getFullYear()} BUILDR. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
